export type ResolveServiceErrorKey = {
  (error: unknown): string | undefined;
  (error: unknown, fallback: string): string;
};

export type AspNetCoreMvcErrorPayload = {
  errorCode?: unknown;
  fieldName?: unknown;
  message?: unknown;
};

export type AspNetCoreMvcResponseStatus = {
  errorCode?: unknown;
  message?: unknown;
  errors?: unknown;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === "object" && !Array.isArray(value);

const asRecord = (value: unknown): Record<string, unknown> | null => (isRecord(value) ? value : null);

const resolveServiceErrorKeyInternal = (error: unknown, fallback?: string): string | undefined => {
  if (typeof error === "string") {
    const trimmed = error.trim();
    if (trimmed.length > 0) {
      return trimmed;
    }
  }
  if (error instanceof Error) {
    const message = error.message?.trim();
    if (message && message.length > 0) {
      return message;
    }
  }
  if (typeof fallback === "string") {
    const trimmed = fallback.trim();
    if (trimmed.length > 0) {
      return trimmed;
    }
  }
  return undefined;
};

export const resolveServiceErrorKey = resolveServiceErrorKeyInternal as ResolveServiceErrorKey;

export const normalizeApiString = (value: unknown): string => {
  if (typeof value !== "string") {
    return "";
  }
  return value.trim();
};

const normalizeErrorMessages = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map(normalizeApiString).filter(Boolean);
  }

  if (typeof value === "string") {
    return [normalizeApiString(value)].filter(Boolean);
  }

  return [];
};

export const parseApiResponsePayload = (value: unknown): unknown => {
  if (typeof value !== "string") {
    return value;
  }

  const trimmed = value.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
    return value;
  }

  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

export const parseApiErrorPayload = (value: unknown): unknown => {
  if (value instanceof Error) {
    return parseApiResponsePayload(value.message);
  }
  return parseApiResponsePayload(value);
};

export const normalizeApiResponseErrors = (value: unknown): AspNetCoreMvcErrorPayload[] => {
  if (Array.isArray(value)) {
    return value.flatMap(entry => {
      if (typeof entry === "string") {
        return [{ message: entry }];
      }
      return entry != null && typeof entry === "object" ? [entry as AspNetCoreMvcErrorPayload] : [];
    });
  }

  if (!isRecord(value)) {
    return [];
  }

  return Object.entries(value).flatMap(([fieldName, messages]) =>
    normalizeErrorMessages(messages).map(message => ({
      fieldName,
      message
    }))
  );
};

export const asResponseStatus = (data: unknown): AspNetCoreMvcResponseStatus | null => {
  const record = asRecord(data);
  if (!record) {
    return null;
  }
  return record;
};

export const mapServiceErrorKeyWithPrefixes = (
  error: unknown,
  fallback: string,
  map: Record<string, string>,
  prefixes: string[]
): string => {
  const key = resolveServiceErrorKey(error, fallback);
  if (prefixes.some(prefix => key.startsWith(`${prefix}.`))) {
    return key;
  }
  return map[key] ?? fallback;
};

export const mapServiceErrorKey = (error: unknown, fallback: string, map: Record<string, string>): string => {
  return mapServiceErrorKeyWithPrefixes(error, fallback, map, []);
};

export const mapServiceErrorKeyOrCodeWithPrefixes = (
  error: unknown,
  fallback: string,
  map: Record<string, string>,
  prefixes: string[]
): string => {
  const key = extractErrorCode(error) || resolveServiceErrorKey(error, fallback);
  if (prefixes.some(prefix => key.startsWith(`${prefix}.`))) {
    return key;
  }
  return map[key] ?? key;
};

export const mapServiceErrorKeyOrCode = (error: unknown, fallback: string, map: Record<string, string>): string => {
  return mapServiceErrorKeyOrCodeWithPrefixes(error, fallback, map, []);
};

const findFirstCode = (errors: AspNetCoreMvcErrorPayload[]): string => {
  return (
    errors
      .map(error => normalizeApiString(error.errorCode) || normalizeApiString(error.message))
      .find(errorCode => errorCode.length > 0) ?? ""
  );
};

export const extractErrorCode = (value: unknown): string => {
  const parsedError = parseApiErrorPayload(value);
  if (typeof parsedError === "string") {
    return normalizeApiString(parsedError);
  }

  const directCode = findFirstCode(normalizeApiResponseErrors(parsedError));
  if (directCode.length > 0) {
    return directCode;
  }

  if (!isRecord(parsedError)) {
    return "";
  }

  const nestedStatus = normalizeApiString(parsedError.errorCode);
  if (nestedStatus.length > 0) {
    return nestedStatus;
  }

  const nestedMessage = normalizeApiString(parsedError.message);
  if (nestedMessage.length > 0) {
    return nestedMessage;
  }

  const responseStatus = asResponseStatus(parsedError.responseStatus);
  const responseStatusCode = extractErrorCode(responseStatus);
  if (responseStatusCode.length > 0) {
    return responseStatusCode;
  }

  const nestedErrors = parsedError.errors;
  if (nestedErrors != null) {
    const errorsCode = findFirstCode(normalizeApiResponseErrors(nestedErrors));
    if (errorsCode.length > 0) {
      return errorsCode;
    }
  }

  return "";
};

export const resolveErrorCode = (errorThrown: unknown, data: unknown): string => {
  const resolvedError = parseApiErrorPayload(errorThrown);
  const resolvedData = parseApiResponsePayload(data);
  const responseStatus = asResponseStatus(resolvedData) ?? asResponseStatus(resolvedError);
  const responseStatusCode = normalizeApiString(responseStatus?.errorCode);
  const responseStatusNestedCode = findFirstCode(normalizeApiResponseErrors(responseStatus?.errors));
  const dataCode = extractErrorCode(resolvedData);
  const errorCode = extractErrorCode(resolvedError);

  if (dataCode.length > 0 && dataCode !== "ValidationException") {
    return dataCode;
  }

  if (responseStatusNestedCode.length > 0) {
    return responseStatusNestedCode;
  }

  if (responseStatusCode.length > 0 && responseStatusCode !== "ValidationException") {
    return responseStatusCode;
  }

  return errorCode || responseStatusCode || dataCode;
};

export const readResponseErrorCode = async (response: Response): Promise<string | null> => {
  try {
    const payload = (await response.json()) as { responseStatus?: { errorCode?: string | null } } | null;
    const code = payload?.responseStatus?.errorCode;
    if (typeof code === "string") {
      const trimmed = code.trim();
      return trimmed.length > 0 ? trimmed : null;
    }
  } catch {
    return null;
  }
  return null;
};
